import React, { Component, Suspense } from 'react'

import { compose } from 'redux'
import { Switch, Route, withRouter } from 'react-router-dom'
import ShiftsWidget from './Widgets/ShiftsWidget'
import ApiErrorCatcher from './Components/shared/ApiErrorCatcher'
import AppCrash from './Components/shared/AppCrash'
import AppBanner from './Components/shared/AppBanner'
import AppLoading from './Components/shared/AppLoading'
import AppManager from './Views/AppManager'
import BasicFormWrapper from './Components/shared/BasicFormWrapper'
import AnonymousFormWrapper from './Components/shared/AnonymousFormWrapper'
import NonProdEnvironmentBanner from './Components/shared/NonProdEnvironmentBanner'
import Cookie from 'js-cookie'
import LoginView from './Views/shared/LoginView'
import PasswordForceUpdateForm from './Components/anonymous/PasswordForceUpdateForm'
import PasswordResetAskForm from './Components/anonymous/PasswordResetAskForm'
import PasswordResetForm from './Components/anonymous/PasswordResetForm'
import SignUpForm from './Components/anonymous/SignUpForm'
import StudentSignUpForm from './Components/anonymous/StudentSignUpForm'
import ConfirmAdditionalEmail from './Components/anonymous/ConfirmAdditionalEmail'
import Theme from './Components/shared/Theme'
import UserSwitch from './Components/anonymous/UserSwitch'
import ChatManager from './Components/shared/ChatManager'
import blue from './themes/blue.json'
import { notification } from 'antd'
import { mapStateToProps, mapDispatchToProps, connect } from './reducers/Dispatchers'
import {
  request,
  getUrlParameterByName,
  getCurrentUrl,
  objectDeepCopy,
  getContextFromRoles,
  filterSectionsAndSetRootSectionOfSchool
} from './utils'
import { Contexts, SchoolRoles, OtherRoles, InstitutionRoles } from './utils/constants'
import { HttpMethods, URIEncode } from './utils/apiHelper'
import appConfig from './config'
import { setLanguage } from './utils/locale'
import Analytics from './utils/analytics'
import { Routes } from './routes'
import ChangeLogNotification from './Views/shared/ChangeLogNotification'
import GlobalProvider from './Providers/GlobalProvider'
import { fetchSchoolOptions } from './utils/api/school'
import GlobalFiltersProvider from './Providers/GlobalFiltersProvider'
import SchoolProvider from './Providers/SchoolProvider'
import AuthCallback from './Views/shared/AuthCallback'
import AuthProvider, { StorageKeys } from './Providers/AuthProvider'
import { HttpStatuses } from './utils/http'
import { Cookies } from './utils/Cookie'

import '../src/utils/FontAwesomeLibrary'
import './assets/index.scss'
import 'antd/dist/antd.css'

// use React.lazy to load the student validation component and to have code splitting
const SectionEvaluationByStudent = React.lazy(() => import('./Components/anonymous/SectionEvaluationByStudent'))

class App extends Component {
  constructor (props) {
    super(props)

    this.state = {
      appCrashedInfo: null,
      showPasswordResetForm: false,
      showOptinsForm: false,
      /**
       * optinsForUser -> if this is true we show only one agreement checkbox, the "recruitment" option is used only for students
       */
      optinsForUser: false,
      ready: false,
      languageSet: false,
      isWidgetRoute: window.location.href.includes('/widget/'),
      schoolOptions: []
    }

    // initialize analytics
    Analytics.initialize()

    this.previouslySelectedLanguage = null
  }

  get passwordChangeDisplayed () {
    const { showPasswordResetForm, showOptinsForm } = this.state
    return showPasswordResetForm || showOptinsForm
  }

  componentDidMount () {
    if (window.location.href.includes('/widget/')) {
      // don't do any of the automatic loading if we are running in "widget" mode
      return
    }
    this.props.setTheme(blue)
    this.checkUserTimerIndex = 0
    this.checkUserTimer = setInterval(async () => {
      if (this.props.getUser.id || this.checkUserTimerIndex === 10) {
        await this.executeSetLanguage(this.props.getActiveLanguage)
        this.setState({ languageSet: true })
        clearInterval(this.checkUserTimer)
      } else {
        this.checkUserTimerIndex += 1
      }
    }, 100)
    this.refreshAppBannerWarning()
    this.timerRefreshAppBannerWarning = setInterval(
      () => this.refreshAppBannerWarning(),
      60000
    )

    const newUserToken = getUrlParameterByName('user-token')
    if (
      typeof newUserToken !== 'undefined' &&
      newUserToken !== null &&
      newUserToken !== ''
    ) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      // pass the search query too
      this.props.history.push({ pathname: Routes.USER_SIGNUP, search: window.location.search })
      return
    }

    const studentToken = getUrlParameterByName('student-token')
    if (
      typeof studentToken !== 'undefined' &&
      studentToken !== null &&
      studentToken !== ''
    ) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      // pass the search query too
      this.props.history.push({ pathname: Routes.STUDENT_SIGNUP, search: window.location.search })
      return
    }

    // check if we have evaluation token. This means a student want's to give the evaluation for the institution
    const evaluationToken = getUrlParameterByName('section-evaluation-token')
    if (
      typeof evaluationToken !== 'undefined' &&
      evaluationToken !== null &&
      evaluationToken !== ''
    ) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      // pass the search query too
      this.props.history.push({ pathname: Routes.SECTION_EVALUATION_BY_STUDENT, search: window.location.search })
      return
    }

    const passwordResetToken = getUrlParameterByName('password-reset-token')
    if (
      typeof passwordResetToken !== 'undefined' &&
      passwordResetToken !== null &&
      passwordResetToken !== ''
    ) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      this.props.history.push({ pathname: Routes.PASSWORD_RESET, search: window.location.search })
      return
    }

    const emailValidationToken = getUrlParameterByName('email-validation-token')
    if (
      typeof emailValidationToken !== 'undefined' &&
      emailValidationToken !== null &&
      emailValidationToken !== ''
    ) {
      Cookie.remove(Cookies.TOKEN)
      this.setState({
        ready: true
      })
      this.props.history.push({ pathname: Routes.CONFIRM_ADDITIONAL_EMAIL, search: window.location.search })
      return
    }

    if (
      typeof Cookie.get(Cookies.TOKEN) !== 'undefined' &&
      Cookie.get(Cookies.TOKEN) !== null &&
      Cookie.get(Cookies.TOKEN) !== ''
    ) {
      const switchUserId = Cookie.get(Cookies.SWITCH_USER)
      if (
        typeof switchUserId !== 'undefined' &&
        switchUserId !== null &&
        switchUserId !== ''
      ) {
        this.getUserContextAndData(
          JSON.parse(Cookie.get(Cookies.TOKEN)),
          undefined,
          true,
          true
        ).then(() => {
          this.autoLoadUser()
        })
      } else {
        this.getUserContextAndData(JSON.parse(Cookie.get(Cookies.TOKEN)))
      }
      return
    } else {
      if (
        ![Routes.PASSWORD_RESET, Routes.PASSWORD_CHANGE, Routes.AUTH_CALLBACK].includes(
          // TODO: When enabling signup add allowed routes here
          this.props.location.pathname
        )
      ) {
        // redirect to login
        this.navigateToLoginPage()
      }
    }

    this.setState({ ready: true })
  }

  componentWillUnmount () {
    clearInterval(this.timerRefreshAppBannerWarning)
    clearInterval(this.checkUserTimerIndex)
  }

  navigateToLoginPage = () => {
    if (!this.state.isWidgetRoute) {
      this.props.history.push(Routes.LOGIN)
    }
  }

  componentDidCatch (error, info) {
    this.setState({
      appCrashedInfo: { error: error, info: info }
    })
  }

  executeSetLanguage = language => {
    return new Promise((resolve) => {
      const { addTranslationForLanguage, setActiveLanguage, getActiveLanguage, getUser } = this.props
      setLanguage({
        language,
        addTranslationForLanguage,
        setActiveLanguage,
        getActiveLanguage,
        getUser: () => { return getUser },
        getPreviousLanguage: previousLanguage => {
          this.previouslySelectedLanguage = getActiveLanguage
        }
      }).then(() => {
        resolve()
      })
    })
  }

  /**
   * This is the same behaviour as we have in UserSwitch component. If we have a previous user id in cookie
   * We get the user data and then we save the user into global state. The switch user view won't be shown
   */
  autoLoadUser = () => {
    request(
      '/api/User/' + Cookie.get(Cookies.SWITCH_USER),
      HttpMethods.GET,
      null,
      this.props.getUser
    ).then(async json => {
      await this.getUserContextAndData(
        this.props.getUser.token,
        json.data.username,
        true
      )

      // set is ready to true
      this.setState({ ready: true })

      // auto navigate to internships
      this.navigateUserToInitialPageAfterLogin()
    })
  };

  /** This is called after login or after switching the user */
  navigateUserToInitialPageAfterLogin = () => {
    const {
      getUser: { roles }
    } = this.props
    /**
     * If the user is a nurse auto display the shifts manager after the internships are loaded
     */
    if (roles.length === 1) {
      if ([InstitutionRoles.NURSE, SchoolRoles.VALIDATOR].includes(roles[0])) {
        this.props.history.push(Routes.SHIFTS_MANAGER)
      } else if (roles[0] === OtherRoles.ADMIN) {
        this.props.history.push(Routes.CHANGELOG_MANAGEMENT)
      } else {
        this.props.history.push(Routes.INTERNSHIPS)
      }
    } else {
      this.props.history.push(Routes.INTERNSHIPS)
    }
  };

  /**
   * This is executed as a callback inside LoginView, after the user is signed in
   */
  handleAfterLogin = () => {
    const {
      getUser: { roles }
    } = this.props
    if (getContextFromRoles(roles) !== OtherRoles.ADMIN) {
      this.navigateUserToInitialPageAfterLogin()
    }
  }

  refreshAppBannerWarning = async () => {
    // We use no-store option on fetch to be sure the app loads the last app-banner.json from the server and not from local disk cache.
    // Before using this options the file would not be updated with the last version and a big part of the users
    // would not get the new banner/maintenance mode.
    const response = await window.fetch(
      getCurrentUrl() + '/config/app-banner.json',
      { cache: 'no-store' }
    )

    let appBanner = null
    try {
      appBanner = await response.json()
    } catch (e) {
      console.error('Error parsing App banner json.')
      return
    }

    if (JSON.stringify(this.props.getAppBanner) === JSON.stringify(appBanner)) { return }

    if (typeof appBanner.message === 'undefined') {
      console.error('App banner "message" not found. Check config file.')
      return
    }
    if (typeof appBanner.type === 'undefined') {
      console.error('App banner "type" not found. Check config file.')
      return
    }
    if (typeof appBanner.maintenance === 'undefined') {
      console.error('App banner "maintenance" not found. Check config file.')
      return
    }
    if (
      appBanner.type !== 'info' &&
      appBanner.type !== 'warning' &&
      appBanner.type !== 'error'
    ) {
      console.error(
        'Unknown app banner type. Only "info", "warning", "error" are permitted.'
      )
      return
    }

    this.props.setAppBanner(appBanner)
  };

  logout = async () => {
    // clear the changeloglist
    this.props.resetChangelog()
    // clear evaluation questions
    this.props.clearEvaluationQuestions()
    // reset the tags
    this.props.resetTags()
    const allDataReady =
      Object.keys(this.props.getDataReady).filter(
        d => !this.props.getDataReady[d]
      ).length === 0
    await Cookie.remove(Cookies.SWITCH_USER)

    if (typeof this.props.getUser.switchUser !== 'undefined') {
      // Preventing any further data storing in the app if the user disconnected since last requests.
      // This should be replaced by a request manager, or something else.
      if (!allDataReady) window.location.reload()

      await this.setState({ ready: false })
      this.getUserContextAndData(this.props.getUser.token)
      return
    }

    await Cookie.remove(Cookies.TOKEN)

    // Preventing any further data storing in the app if the user disconnected since last requests.
    // This should be replaced by a request manager, or something else.
    if (!allDataReady) window.location.reload()

    await this.setState({
      ready: true
    })

    const accountId = JSON.parse(window.localStorage.getItem(StorageKeys.ACCOUNT_ID))
    window.localStorage.removeItem(StorageKeys.ACCOUNT_ID)

    if (accountId) {
      const logoutCallbackUrl = encodeURIComponent(`${window.location.protocol}//${window.location.host}/login`)
      window.location.href = `${appConfig.opalAuthServeurWebapp}?account_id=${accountId}&logout=true&logout_callback=${logoutCallbackUrl}`
    } else {
      this.navigateToLoginPage()
    }

    this.props.setUser(null)
  };

  handleSignUp = userData => {
    const body = {
      username: userData.username,
      password: userData.password,
      password_confirm: userData.password,
      lastname: userData.lastname,
      firstname: userData.firstname,
      email: userData.email
    }

    request('/signup', HttpMethods.POST, body)
      .then(json => {
        this.navigateToLoginPage()
      })
  };

  getUserContextAndData = (
    token,
    switchedUsername,
    preventSettingReadyToTrue = false,
    preventLoadingSuperAdmin = false
  ) => {
    return new Promise((resolve, reject) => {
      request('/user/me', HttpMethods.GET, null, {
        token: token,
        switchUser:
          typeof switchedUsername === 'undefined' ? null : URIEncode(switchedUsername)
      })
        .then(async json => {
          if (typeof json.code !== 'undefined' && json.code === HttpStatuses.UNAUTHORIZED) {
            // Session yummy cookie has expired :(
            this.logout()
            return
          }
          if (json === 'password_change') {
            this.props.setUser({ token: token })
            this.setState({ ready: true, showPasswordResetForm: true })
            // this.props.history.push(routes.PASSWORD_CHANGE)
            return
          }
          if (json === 'password_change_set_optins' || json === 'password_change_set_optins_user') {
            this.props.setUser({ token: token })
            this.setState({
              ready: true,
              showPasswordResetForm: true,
              showOptinsForm: true,
              optinsForUser: json === 'password_change_set_optins_user'
            })
            return
          }
          if (json === 'set_optins' || json === 'set_optins_user') {
            this.props.setUser({ token: token })
            this.setState({
              ready: true,
              showPasswordResetForm: false,
              showOptinsForm: true,
              optinsForUser: json === 'set_optins_user'
            })
            return
          }

          const user = json.data

          // check what's the user's language and use it
          if (user.language !== null !== undefined && user.language !== null && user.language !== this.props.getActiveLanguage) {
            this.executeSetLanguage(user.language)
          }

          const context = getContextFromRoles(user.roles)
          user.context = context
          user.token = token
          if (typeof switchedUsername !== 'undefined') {
            user.switchUser = switchedUsername
          }

          await this.props.setUser(user)
          Analytics.setUserDetails(user, this.props.getActiveLanguage)

          if (this.passwordChangeDisplayed) {
            this.setState({ showPasswordResetForm: false, showOptinsForm: false })
          }

          if (!preventSettingReadyToTrue) {
            this.setState({
              ready: true
            })
          }

          if (context === Contexts.ADMIN && !preventSettingReadyToTrue) {
            this.props.history.push(Routes.SWITCH_USER)
            return
          }

          switch (user.context) {
            case Contexts.STUDENT:
              this.loadStudentData()
              break
            case Contexts.SCHOOL:
              this.loadSchoolData()
              break
            case Contexts.INSTITUTION:
              this.loadHospitalData()
              break
            case Contexts.ADMIN:
              if (!preventLoadingSuperAdmin) {
                this.loadSuperAdminData()
              }
              break
          }

          resolve()
        })
        .catch(err => {
          Cookie.remove(Cookies.TOKEN)
          reject(err)
        })
    })
  };

  loadSchoolData = async () => {
    this.props.setDataReady({
      schools: false,
      institutions: true,
      students: false,
      internships: true // Internships fetching is not done here for schools, as it is loaded later in the timeline when all other data are ready.
    })

    await this.updateReducer(
      `/schools/0/${appConfig.apiPageSize}`,
      'schools',
      this.props.setSchools,
      schools => {
        if (schools.length) {
          let school = schools[0]
          school = filterSectionsAndSetRootSectionOfSchool(
            school,
            this.props.t('Without section')
          )
          let sections = school.sections

          // User coordinated sections from /user are missing data, we replace them by the ones from /api
          if (this.props.getUser.roles.indexOf(SchoolRoles.COORDINATOR) > -1) {
            const mySections = this.props.getUser.coordinatedSections
            sections = sections.filter(s => {
              for (let i = 0; i < mySections.length; i++) {
                if (s.id === mySections[i].id) {
                  return true
                }
              }
              return false
            })
          }

          school.sections = sections
          return [school]
        }
        return []
      }
    )

    const user = this.props.getUser

    if (this.props.getSchools.length > 0) {
      const schoolId = this.props.getSchools[0].id

      this.props.fetchSchoolSectionsBySchool(schoolId, user)
      this.props.fetchInstitutionsWithActivatedPauseQuotaOption(schoolId, user)
      fetchSchoolOptions(user, { id: schoolId }).then(json => {
        if (json && json.data) {
          this.setState({ schoolOptions: json.data })
        }
      })
    }

    this.props.fetchCanSchoolEditSchedulesByInstitution(user)
    this.props.fetchInstitutionConstraints(user)
    this.props.fetchTags()

    this.updateReducer(
      `/students/0/${appConfig.apiPageSize}`,
      'students',
      this.props.setStudents,
      students => {
        return students
      }
    )
  };

  loadHospitalData = async () => {
    const currentUser = this.props.getUser

    this.props.setDataReady({
      schools: currentUser.roles.includes(InstitutionRoles.INSTITUTION_GROUP_ADMIN),
      institutions: true,
      students: true,
      internships: true // Internship fetching is done here for hospitals, as it is loaded later in the timeline when all other data are ready.
    })

    if (!currentUser.roles.includes(InstitutionRoles.INSTITUTION_GROUP_ADMIN)) {
      await this.updateReducer(
        `/schools/0/${appConfig.apiPageSize}`,
        'schools',
        this.props.setSchools
      )

      const institution = currentUser.institutions[0]

      this.props.fetchTags()

      if (!currentUser.roles.includes(SchoolRoles.VALIDATOR)) {
        this.props.fetchBadges(institution.id, currentUser)
        this.props.fetchInstitutionQuotas(institution.id, currentUser)
        this.props.setSelectedInstitution(institution)
      }
    }
  };

  loadSuperAdminData = () => {
    this.props.setDataReady({
      schools: false,
      institutions: false,
      students: true,
      internships: true // Internship fetching is done here for hospitals, as it is loaded later in the timeline when all other data are ready.
    })

    this.updateReducer(
      `/schools/0/${appConfig.apiPageSize}`,
      'schools',
      this.props.setSchools
    )
  }

  loadStudentData = () => {
    this.props.setDataReady({
      schools: true, // School fetching is not done for students, as current school is never displayed for a student.
      institutions: true, // Institution fetching not done for student, as it is loaded later in the internship display when all other data are ready.
      students: true // Students fetching is used for student, as the information is already loaded with /user/me at connection.
    })
  };

  updateReducer = async (
    requestUrl,
    getter,
    setter,
    callbackBeforeStoringData,
    callbackAfterStoringData
  ) => {
    let json = await request(requestUrl, HttpMethods.GET, null, this.props.getUser)
    let data = json.data

    while (json.nextPage) {
      json = await request(json.nextPage, HttpMethods.GET, null, this.props.getUser)
      data = data.concat(json.data)
    }

    if (typeof callbackBeforeStoringData !== 'undefined') {
      data = await callbackBeforeStoringData(data)
    }

    // Preventing any further data storing in the app if the user disconnected since last requests.
    // This should be replaced by a request manager, or something else.
    if (this.props.getUser !== null) {
      setter(data)

      const dataReady = objectDeepCopy(this.props.getDataReady)
      dataReady[getter] = true
      this.props.setDataReady(dataReady)
      if (typeof callbackAfterStoringData === 'function') {
        callbackAfterStoringData()
      }
    }
  };

  handleWrongLink = () => {
    this.setState({
      ready: true
    })

    notification.error({
      message: this.props.t('This link does not exist or has expired.'),
      placement: 'bottomRight'
    })
  };

  render () {
    if ((!this.state.ready || !this.state.languageSet) && !this.state.isWidgetRoute) {
      return <AppLoading />
    }

    const inMaintenanceMode =
      this.props.getAppBanner !== null &&
      typeof this.props.getAppBanner.maintenance &&
      this.props.getAppBanner.maintenance

    let context = null
    if (this.props.getUser.roles) {
      context = getContextFromRoles(this.props.getUser.roles)
    }

    return (
      <Theme variables={this.props.getTheme}>
        <AuthProvider>
          <GlobalProvider>
            <GlobalFiltersProvider>
              <AppCrash log={this.state.appCrashedInfo} onBack={() => this.setState({ appCrashedInfo: null })} />
              <ApiErrorCatcher />
              {inMaintenanceMode && (
                <div style={{ height: '100vh', padding: '10vw' }}>
                  <AppBanner />
                </div>
              )}
              {!inMaintenanceMode && (this.state.showPasswordResetForm || this.state.showOptinsForm) &&
                <BasicFormWrapper>
                  <PasswordForceUpdateForm
                    onPasswordChanged={(token) => {
                      Cookie.set(Cookies.TOKEN, JSON.stringify(token), { expires: 7 })
                      this.getUserContextAndData(token).then(() => {
                        this.navigateUserToInitialPageAfterLogin()
                      })
                    }}
                    showOptinsSelection={this.state.showOptinsForm}
                    showPasswordChangeForm={this.state.showPasswordResetForm}
                    userIsNoStudent={this.state.optinsForUser}
                  />
                </BasicFormWrapper>}
              {!inMaintenanceMode && this.state.isWidgetRoute && (
                <Switch>
                  <Route path={Routes.SHIFTS_WIDGET}>
                    <ShiftsWidget />
                  </Route>
                </Switch>)}
              {!inMaintenanceMode && !this.state.isWidgetRoute && (
                <Switch>
                  <Route path={Routes.LOGIN}>
                    {!this.passwordChangeDisplayed &&
                      <LoginView
                        onGetUserContextAndData={this.getUserContextAndData} //eslint-disable-line
                        onAfterLogin={this.handleAfterLogin}
                      />}
                  </Route>
                  <Route path={Routes.AUTH_CALLBACK}>
                    {!this.passwordChangeDisplayed &&
                      <AuthCallback
                        onGetUserContextAndData={this.getUserContextAndData} //eslint-disable-line
                        onAfterLogin={this.handleAfterLogin}
                      />}
                  </Route>
                  <Route path={Routes.PASSWORD_RESET_REQUEST}>
                    {!this.passwordChangeDisplayed &&
                      <AnonymousFormWrapper topBanner={<NonProdEnvironmentBanner />}>
                        <PasswordResetAskForm
                          onBack={() => {
                            this.navigateToLoginPage()
                          }}
                        />
                      </AnonymousFormWrapper>}
                  </Route>
                  <Route path={Routes.PASSWORD_RESET}>
                    {!this.passwordChangeDisplayed &&
                      <BasicFormWrapper>
                        <PasswordResetForm
                          onBack={() => {
                            this.navigateToLoginPage()
                          }}
                        />
                      </BasicFormWrapper>}
                  </Route>
                  <Route path={Routes.CONFIRM_ADDITIONAL_EMAIL}>
                    {!this.passwordChangeDisplayed &&
                      <BasicFormWrapper>
                        <ConfirmAdditionalEmail
                          onBack={() => {
                            this.navigateToLoginPage()
                          }}
                        />
                      </BasicFormWrapper>}
                  </Route>
                  <Route path={Routes.SWITCH_USER}>
                    {!this.passwordChangeDisplayed &&
                      <BasicFormWrapper>
                        <UserSwitch
                        onLogout={this.logout} //eslint-disable-line
                          switchUser={user => {
                            this.getUserContextAndData(
                              this.props.getUser.token,
                              URIEncode(user.username),
                              true
                            ).then(this.navigateUserToInitialPageAfterLogin)
                          }}
                        />
                      </BasicFormWrapper>}
                  </Route>
                  <Route path={Routes.SIGNUP}>
                    {!this.passwordChangeDisplayed &&
                      <BasicFormWrapper>
                        <SignUpForm onSignUp={this.handleSignUp} />
                      </BasicFormWrapper>}
                  </Route>
                  <Route path={Routes.STUDENT_SIGNUP}>
                    {!this.passwordChangeDisplayed &&
                      <BasicFormWrapper>
                        <StudentSignUpForm
                          onWrongToken={this.handleWrongLink}
                    onSignUp={this.getUserContextAndData} //eslint-disable-line
                        />
                      </BasicFormWrapper>}
                  </Route>
                  <Route path={Routes.USER_SIGNUP}>
                    {!this.passwordChangeDisplayed &&
                      <BasicFormWrapper>
                        <StudentSignUpForm
                          onWrongToken={this.handleWrongLink}
                    onSignUp={(token) => {this.getUserContextAndData(token).then(() => {this.navigateUserToInitialPageAfterLogin()})} } //eslint-disable-line
                          isUserSignup
                        />
                      </BasicFormWrapper>}
                  </Route>
                  <Route path={Routes.SECTION_EVALUATION_BY_STUDENT}>
                    <BasicFormWrapper isWide>
                      <Suspense fallback={<div style={{ display: 'flex', flexDirection: 'column', minHeight: '300px', position: 'relative' }} />}>
                        <SectionEvaluationByStudent sectionEvaluationByStudent />
                      </Suspense>
                    </BasicFormWrapper>
                  </Route>
                  <Route path='/*'>
                    {!this.passwordChangeDisplayed && (
                      <SchoolProvider>
                        <AppManager
                        onLogout={this.logout} //eslint-disable-line
                          schoolOptions={this.state.schoolOptions}
                        />
                      </SchoolProvider>
                    )}
                  </Route>
                </Switch>
              )}
              {context && ![Contexts.NONE, Contexts.ADMIN].includes(context) && <ChangeLogNotification />}
              <ChatManager />
            </GlobalFiltersProvider>
          </GlobalProvider>
        </AuthProvider>
      </Theme>
    )
  }
}

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(App)
